import React, { useState, useEffect } from 'react';
import { RiEditBoxLine } from "react-icons/ri"; 
import { toast } from 'react-toastify';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom'; 
import styles from '../products/ProductForm.module.css'; 
import SerialNumberBadge from '../../components/product/SerialNumberBadge'; // Import the badge

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EditFaultyItem = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        productName: "", 
        productCode: "", 
        managedBySerial: false, 
        selectedSerialNumbers: [], // Changed from faultySerialNumber
        faultyQuantity: 0, 
        faultDescription: "",
        severity: "Low",
        status: "Faulty", 
    });

    const [availableSerials, setAvailableSerials] = useState([]);
    const [currentSerialSelection, setCurrentSerialSelection] = useState("");
    const [originalProductId, setOriginalProductId] = useState(null); // To store the product ID for fetching serials


    useEffect(() => {
        const itemToEdit = location.state?.faultyItemData;
        if (itemToEdit) {
            // Ensure productId is the actual ID string, not an object
            const productId = typeof itemToEdit.product === 'object' && itemToEdit.product !== null ? itemToEdit.product._id : itemToEdit.product;
            setOriginalProductId(productId); 
            setFormData({
                productName: itemToEdit.productName || "N/A",
                productCode: itemToEdit.productCode || "N/A",
                managedBySerial: itemToEdit.managedBySerial || false,
                selectedSerialNumbers: itemToEdit.faultySerialNumbers || [],
                faultyQuantity: itemToEdit.managedBySerial ? (itemToEdit.faultySerialNumbers?.length || 0) : (itemToEdit.faultyQuantity || 0),
                faultDescription: itemToEdit.faultDescription || "",
                severity: itemToEdit.severity || "Low",
                status: itemToEdit.status || "Faulty",
            });
            if (itemToEdit.managedBySerial && productId) {
                // Fetch product details to get all available serial numbers for the dropdown
                axios.get(`${BACKEND_URL}/api/products/${productId}`)
                    .then(response => {
                        const productDetails = response.data;
                        // The available serials for selection should be those currently on the product
                        // plus those already assigned to this faulty item (in case user wants to remove and re-add)
                        const allPossibleSerialsForThisProduct = Array.from(new Set([...(productDetails.serialNumberAvailable || []), ...(itemToEdit.faultySerialNumbers || [])]));
                        setAvailableSerials(allPossibleSerialsForThisProduct);
                    })
                    .catch(error => {
                        console.error("Error fetching product details for serial numbers:", error);
                        toast.error("Could not load available serial numbers for this product.");
                    });
            }
        } else {
            toast.error("Faulty item data not found. Please select the item from the list.");
            navigate('/FaultyItemsPage'); 
        }
    }, [navigate, location.state]); 

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => {
            const newData = { ...prevData, [name]: value };
            // If faultyQuantity is changed for a non-serialized item, update it
            if (name === 'faultyQuantity' && !newData.managedBySerial) {
                newData.faultyQuantity = parseInt(value, 10) || 0;
            }
            return newData;
        });
    };

    const handleAddSerialNumber = () => {
        if (!currentSerialSelection) {
            toast.error("Please select a serial number to add.");
            return;
        }
        if (formData.selectedSerialNumbers.includes(currentSerialSelection)) {
            toast.error("Serial number already added to this faulty item.");
            return;
        }
        setFormData(prevData => ({
            ...prevData,
            selectedSerialNumbers: [...prevData.selectedSerialNumbers, currentSerialSelection],
            faultyQuantity: prevData.selectedSerialNumbers.length + 1 // Update quantity for serialized items
        }));
        setCurrentSerialSelection(""); // Reset dropdown selection
    };

    const handleRemoveSerialNumber = (serialToRemove) => {
        setFormData(prevData => {
            const updatedSerials = prevData.selectedSerialNumbers.filter(sn => sn !== serialToRemove);
            return {
                ...prevData,
                selectedSerialNumbers: updatedSerials,
                faultyQuantity: updatedSerials.length // Update quantity for serialized items
            };
        });
    };

    const validateForm = () => {
        const { faultDescription, severity, status, managedBySerial, faultyQuantity, selectedSerialNumbers } = formData;
        if (!faultDescription.trim() || !severity || !status) {
            toast.error("Fault description, severity, and status are required.");
            return false;
        }
        if (status === 'Repaired') {
            toast.error("Status cannot be set to 'Repaired' through this form. Use the 'Mark as Repaired' action from the list.");
            return false;
        }
        if (managedBySerial && selectedSerialNumbers.length === 0) {
            toast.error("At least one serial number must be selected for serialized items.");
            return false;
        }
        if (!managedBySerial && (isNaN(parseInt(faultyQuantity, 10)) || parseInt(faultyQuantity, 10) <= 0)) {
             toast.error("Faulty quantity must be a positive number for non-serialized items.");
             return false;
        }
        return true;
    };

    const handleUpdateFaultyItemSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsLoading(true);
        const { faultDescription, severity, status, managedBySerial, faultyQuantity, selectedSerialNumbers } = formData;

        const updateData = {
            faultDescription,
            severity,
            status,
        };

        if (!managedBySerial) {
            updateData.faultyQuantity = Number(faultyQuantity);
        } else {
            updateData.serialNumbers = selectedSerialNumbers; // Send the array of selected serial numbers
        }

        try {
            await axios.put(`${BACKEND_URL}/api/faulty-items/${location.state?.faultyItemData._id}`, updateData);
            toast.success("Faulty item updated successfully!");
            navigate('/FaultyItemsPage');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update faulty item.');
            console.error("Failed to update faulty item:", error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={styles.formContainer}>
            <div className={styles.formCard}>
                <div className={styles.topLine}></div>
                <button
                    className={styles.backButton}
                    onClick={() => navigate('/FaultyItemsPage')}
                >
                    ← Back to Faulty Items
                </button>
                <div className={styles.cardHeader}>
                    <RiEditBoxLine size={28} />
                    <h2 className={styles.title}>Edit Faulty Item</h2>
                </div>

                <form onSubmit={handleUpdateFaultyItemSubmit}>
                    {/* Display Product Info - Read Only */}
                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Product Name</label>
                            <input type="text" value={formData.productName} className={`${styles.formInput} ${styles.readOnlyInput}`} readOnly />
                        </div>
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Product Code</label>
                            <input type="text" value={formData.productCode} className={`${styles.formInput} ${styles.readOnlyInput}`} readOnly />
                        </div>
                    </div>
                    <div className={styles.formRow}>
                        {formData.managedBySerial ? (
                            <div className={styles.formGroup}>
                                <label className={`${styles.formLabel} ${styles.requiredLabel}`}>
                                    Serial Number(s)
                                </label>
                                <div className={styles.serialInputRow}>
                                    <select
                                        name="currentSerialSelection"
                                        value={currentSerialSelection}
                                        onChange={(e) => setCurrentSerialSelection(e.target.value)}
                                        className={styles.formInput}
                                        disabled={availableSerials.filter(s => !formData.selectedSerialNumbers.includes(s)).length === 0}
                                    >
                                        <option value="">-- Select a Serial Number to Add --</option>
                                        {availableSerials
                                            .filter(serial => !formData.selectedSerialNumbers.includes(serial))
                                            .map(serial => (
                                                <option key={serial} value={serial}>
                                                    {serial}
                                                </option>
                                            ))}
                                    </select>
                                    <button
                                        type="button"
                                        onClick={handleAddSerialNumber}
                                        className={styles.addSerialBtn}
                                        disabled={!currentSerialSelection}
                                    >
                                        Add
                                    </button>
                                </div>
                                <div className={`${styles.serialTags} ${styles.largerSerialBadge}`} style={{ marginTop: '10px' }}>
                                    {formData.selectedSerialNumbers.map((serial) => (
                                        <SerialNumberBadge
                                            key={serial}
                                            serialNumber={serial}
                                            onRemove={() => handleRemoveSerialNumber(serial)}
                                            isRemovable={true}
                                        />
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Faulty Quantity</label>
                                <input type="number" name="faultyQuantity" value={formData.faultyQuantity} onChange={handleInputChange} className={styles.formInput} min="0" /> {/* Made editable */}
                            </div>
                        )}
                        {formData.managedBySerial && (
                            <div className={styles.formGroup}>
                                <label className={`${styles.formLabel} ${styles.requiredLabel}`}>
                                    Faulty Quantity (auto)
                                </label>
                                <input type="number" className={styles.formInput} name="quantity" value={formData.selectedSerialNumbers.length} readOnly />
                            </div>
                        )}

                    </div>

                    {/* Editable Fields */}
                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label className={`${styles.formLabel} ${styles.requiredLabel}`}>Severity</label>
                            <select name="severity" value={formData.severity} onChange={handleInputChange} className={styles.formInput} required>
                                <option value="Low">Low</option>
                                <option value="Medium">Medium</option>
                                <option value="High">High</option>
                                <option value="Critical">Critical</option>
                            </select>
                        </div>
                        <div className={styles.formGroup}>
                            <label className={`${styles.formLabel} ${styles.requiredLabel}`}>Status</label>
                            <select name="status" value={formData.status} onChange={handleInputChange} className={styles.formInput} required>
                                <option value="Faulty">Faulty</option>
                                <option value="Under Repair">Under Repair</option>
                                <option value="Unrepairable">Unrepairable</option>
                                {/* 'Repaired' status is handled by a separate action */}
                            </select>
                        </div>
                    </div>

                    <div className={styles.formRow}>
                        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                            <label className={`${styles.formLabel} ${styles.requiredLabel}`}>Fault Description</label>
                            <textarea name="faultDescription" value={formData.faultDescription} onChange={handleInputChange} className={styles.textareaInput} rows="4" required />
                        </div>
                    </div>

                    <button type="submit" className={styles.submitBtn} disabled={isLoading}>
                        {isLoading ? "Updating..." : "Update Faulty Item"}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default EditFaultyItem;